import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardCopy, ExternalLink, Search, X } from 'lucide-react';
import { HostlinksEvent } from '../../types';
import { eventsApi, HOSTLINKS_UPCOMING_DAYS } from '../../services/api';
import { getApiErrorParts, logSafeAxiosError } from '../../utils/safeAxiosLog';
import { eventLabel, matchesHostlinksEvent } from './eventBuildUtils';
import { useAuth } from '../../context/AuthContext';

function formatLastUpdated(iso: string | null): string {
  if (!iso) return 'Never updated';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never updated';
  return `Last updated ${d.toLocaleString()}`;
}

interface Props {
  selected: HostlinksEvent | null;
  onSelect: (event: HostlinksEvent) => void;
  onClear: () => void;
  alreadyQueued?: React.ReactNode;
}

function extractEventsArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    if (Array.isArray(o.events)) return o.events;
    if (Array.isArray(o.data)) return o.data;
    if (o.id != null) return [o];
  }
  return [];
}

const HostlinksEventPicker: React.FC<Props> = ({
  selected,
  onSelect,
  onClear,
  alreadyQueued,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [events, setEvents] = useState<HostlinksEvent[]>([]);
  const [eventsError, setEventsError] = useState<{ headline: string; detail?: string } | null>(null);
  const [eventsWarning, setEventsWarning] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [eventQuery, setEventQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [browsing, setBrowsing] = useState(!selected);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [pasteJson, setPasteJson] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<Window | null>(null);

  const filteredEvents = useMemo(
    () => events.filter((e) => matchesHostlinksEvent(e, eventQuery)),
    [events, eventQuery]
  );

  useEffect(() => {
    setHighlight(0);
  }, [eventQuery, filteredEvents.length]);

  const loadUpcoming = useCallback(async () => {
    setEventsLoading(true);
    setEventsError(null);
    setEventsWarning(null);
    try {
      const res = await eventsApi.getUpcoming();
      setEvents(res.data.events ?? []);
      setLastUpdatedAt(res.data.updatedAt ?? null);
      if (res.data.warning) setEventsWarning(res.data.warning);
    } catch (error) {
      logSafeAxiosError('Error fetching Hostlinks events', error);
      setEvents([]);
      setEventsError(getApiErrorParts(error, 'Could not load Hostlinks events'));
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUpcoming();
  }, [loadUpcoming]);

  useEffect(() => {
    if (browsing) inputRef.current?.focus();
  }, [browsing]);

  useEffect(() => {
    setBrowsing(!selected);
  }, [selected?.id]);

  const pick = (event: HostlinksEvent) => {
    onSelect(event);
    setEventQuery('');
    setBrowsing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (selected) setBrowsing(false);
      else setEventQuery('');
      return;
    }
    if (filteredEvents.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filteredEvents.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filteredEvents[highlight];
      if (target) pick(target);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const openBotCheckPopup = async () => {
    setSyncStatus(null);
    try {
      const cred = await eventsApi.getBrowserSyncCredentials();
      const url = `${cred.data.baseUrl.replace(/\/+$/, '')}/`;
      popupRef.current = window.open(url, 'hostlinks-bot-check', 'width=920,height=720');
      if (!popupRef.current) {
        setSyncStatus('Popup blocked — allow popups for EmailCraft, then try again.');
        return;
      }
      setSyncStatus(
        'Popup opened on your PC’s IP (not the VPS). Complete the “One moment / Please wait” check if shown, then click Continue below.'
      );
    } catch (error) {
      logSafeAxiosError('browser-sync credentials', error);
      const parts = getApiErrorParts(error, 'Could not load Hostlinks credentials');
      setSyncStatus(parts.detail ? `${parts.headline}\n${parts.detail}` : parts.headline);
    }
  };

  const fetchAndCacheFromBrowser = async () => {
    setSyncBusy(true);
    setSyncStatus('Fetching events from Hostlinks in this browser…');
    try {
      const cred = await eventsApi.getBrowserSyncCredentials();
      const base = cred.data.baseUrl.replace(/\/+$/, '');
      const url = `${base}${cred.data.eventsPath}`;
      let body: unknown;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'X-HL-Key': cred.data.apiKey,
            Accept: 'application/json',
          },
          credentials: 'include',
        });
        const text = await res.text();
        const lower = text.toLowerCase();
        if (
          lower.includes('please wait while your request is being verified') ||
          lower.includes('one moment, please')
        ) {
          setShowPaste(true);
          setSyncStatus(
            'Still getting the bot-check page from this browser. Finish the check in the popup, wait a few seconds, then try Continue again — or paste JSON below.'
          );
          return;
        }
        if (!res.ok) {
          setShowPaste(true);
          setSyncStatus(`Hostlinks returned HTTP ${res.status}. You can paste JSON instead.`);
          return;
        }
        body = text ? JSON.parse(text) : null;
      } catch (err) {
        setShowPaste(true);
        const msg = err instanceof Error ? err.message : String(err);
        setSyncStatus(
          `Browser fetch failed (${msg}). Hostlinks may block cross-origin requests (CORS). Paste the JSON response below, or wait for WAF allowlisting of the VPS.`
        );
        return;
      }

      const rows = extractEventsArray(body);
      if (rows.length === 0) {
        setShowPaste(true);
        setSyncStatus('No events in the Hostlinks response. Paste JSON manually if needed.');
        return;
      }

      const saved = await eventsApi.postBrowserSync({
        days: cred.data.days,
        events: rows as Record<string, unknown>[],
      });
      setSyncStatus(`Synced ${saved.data.count} events into EmailCraft.`);
      setSyncOpen(false);
      popupRef.current?.close();
      await loadUpcoming();
    } catch (error) {
      logSafeAxiosError('browser-sync fetch', error);
      const parts = getApiErrorParts(error, 'Browser sync failed');
      setShowPaste(true);
      setSyncStatus(parts.detail ? `${parts.headline}\n${parts.detail}` : parts.headline);
    } finally {
      setSyncBusy(false);
    }
  };

  const copyConsoleSnippet = async () => {
    setSyncBusy(true);
    try {
      const cred = await eventsApi.getBrowserSyncCredentials();
      const path = cred.data.eventsPath.startsWith('/')
        ? cred.data.eventsPath
        : `/${cred.data.eventsPath}`;
      const key = cred.data.apiKey.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      const snippet = [
        `// Paste into the Hostlinks tab console (F12 → Console), then Enter`,
        `(async () => {`,
        `  const r = await fetch('${path}', {`,
        `    headers: { 'X-HL-Key': '${key}', Accept: 'application/json' },`,
        `    credentials: 'include',`,
        `  });`,
        `  const t = await r.text();`,
        `  if (!r.ok) throw new Error('HTTP ' + r.status + ': ' + t.slice(0, 200));`,
        `  try {`,
        `    await navigator.clipboard.writeText(t);`,
        `    console.log('OK — copied ' + t.length + ' chars. Paste into EmailCraft.');`,
        `  } catch (e) {`,
        `    console.log('Clipboard blocked — select/copy the JSON below:');`,
        `    console.log(t);`,
        `  }`,
        `})().catch(e => console.error(e));`,
      ].join('\n');
      await navigator.clipboard.writeText(snippet);
      setShowPaste(true);
      setSyncStatus(
        'Snippet copied. In the Hostlinks tab: F12 → Console → paste → Enter. Then paste the JSON into the box below (clipboard should already hold it).'
      );
    } catch (error) {
      logSafeAxiosError('copy console snippet', error);
      const parts = getApiErrorParts(error, 'Could not build console snippet');
      setSyncStatus(parts.detail ? `${parts.headline}\n${parts.detail}` : parts.headline);
    } finally {
      setSyncBusy(false);
    }
  };

  const submitPaste = async () => {
    setSyncBusy(true);
    try {
      const parsed = JSON.parse(pasteJson) as unknown;
      const rows = extractEventsArray(parsed);
      if (rows.length === 0) {
        setSyncStatus('Paste a JSON array of events (or { "events": [...] }).');
        return;
      }
      const saved = await eventsApi.postBrowserSync({ events: rows as Record<string, unknown>[] });
      setSyncStatus(`Synced ${saved.data.count} events from paste.`);
      setSyncOpen(false);
      setPasteJson('');
      await loadUpcoming();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setSyncStatus(`Paste failed: ${msg}`);
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div className="h-full bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-xs font-medium text-gray-700 dark:text-slate-200">Event</p>
        <div className="flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5 text-[11px]">
          {eventsLoading && lastUpdatedAt === null ? (
            <span className="font-medium text-gray-400 dark:text-slate-500">Loading…</span>
          ) : (
            <span
              className="font-medium text-gray-600 dark:text-slate-300"
              title={lastUpdatedAt ? new Date(lastUpdatedAt).toISOString() : undefined}
            >
              {formatLastUpdated(lastUpdatedAt)}
            </span>
          )}
          {isAdmin && (
            <>
              <span className="text-gray-300 dark:text-slate-600" aria-hidden="true">
                |
              </span>
              <button
                type="button"
                onClick={() => {
                  setSyncOpen(true);
                  setSyncStatus(null);
                  setShowPaste(false);
                }}
                className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                title="Pass the Hostlinks bot check in your browser, then cache events for EmailCraft"
              >
                Sync via browser
              </button>
            </>
          )}
          <span className="text-gray-400 dark:text-slate-500">({HOSTLINKS_UPCOMING_DAYS} days out)</span>
          {selected && !browsing && (
            <button
              type="button"
              onClick={() => setBrowsing(true)}
              className="font-medium text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-300"
            >
              Clear / Change
            </button>
          )}
        </div>
      </div>

      {selected && !browsing ? (
        <div className="flex-1 flex flex-col min-h-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {[selected.location || eventLabel(selected), selected.dates_display, selected.type]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {(selected.city || selected.state) && (
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {[selected.city, selected.state].filter(Boolean).join(', ')}
            </p>
          )}
          {alreadyQueued && <div className="mt-3 min-h-0 overflow-y-auto">{alreadyQueued}</div>}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {eventsLoading && (
            <p className="text-sm text-gray-500 dark:text-slate-400">Loading events…</p>
          )}
          {eventsError && (
            <div className="mb-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2 text-xs text-red-800 dark:text-red-200">
              <p className="font-medium">{eventsError.headline}</p>
              {eventsError.detail && (
                <p className="mt-1 whitespace-pre-wrap break-words opacity-90">{eventsError.detail}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => void loadUpcoming()} className="underline">
                  Retry
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setSyncOpen(true);
                      setSyncStatus(null);
                      setShowPaste(false);
                    }}
                    className="underline font-medium"
                  >
                    Sync via browser
                  </button>
                )}
              </div>
            </div>
          )}
          {eventsWarning && !eventsError && (
            <p className="mb-2 text-[11px] text-amber-800 dark:text-amber-200 whitespace-pre-wrap">
              {eventsWarning}
            </p>
          )}
          {!eventsLoading && !eventsError && (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-slate-500 pointer-events-none" />
                <input
                  ref={inputRef}
                  type="search"
                  value={eventQuery}
                  onChange={(e) => setEventQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search location, city, date…"
                  className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white pl-8 py-1.5 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  aria-autocomplete="list"
                  aria-controls="hostlinks-event-list"
                  aria-activedescendant={
                    filteredEvents[highlight]
                      ? `hostlinks-event-${filteredEvents[highlight].id}`
                      : undefined
                  }
                />
              </div>
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                {eventQuery.trim()
                  ? `${filteredEvents.length} of ${events.length}`
                  : `${events.length} upcoming`}
                {selected && (
                  <button
                    type="button"
                    onClick={() => setBrowsing(false)}
                    className="ml-2 underline"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onClear();
                    setBrowsing(true);
                  }}
                  className="ml-2 underline"
                >
                  Clear event
                </button>
              </p>
              <ul
                id="hostlinks-event-list"
                ref={listRef}
                role="listbox"
                className="mt-1.5 flex-1 min-h-0 max-h-52 overflow-y-auto rounded-md border border-gray-200 dark:border-slate-600 divide-y divide-gray-100 dark:divide-slate-700"
              >
                {filteredEvents.map((event, idx) => (
                  <li key={event.id} data-idx={idx} role="option" aria-selected={idx === highlight}>
                    <button
                      type="button"
                      id={`hostlinks-event-${event.id}`}
                      onClick={() => pick(event)}
                      onMouseEnter={() => setHighlight(idx)}
                      className={`w-full text-left px-2.5 py-2 text-sm ${
                        idx === highlight
                          ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-900 dark:text-blue-100'
                          : 'bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <span className="font-medium text-gray-900 dark:text-white">
                        {eventLabel(event)}
                      </span>
                      {(event.city || event.state) && (
                        <span className="block text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
                          {[event.city, event.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {filteredEvents.length === 0 && events.length === 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  No Hostlinks events stored yet. Wait for the plugin to push, or use Sync via browser.
                </p>
              )}
              {filteredEvents.length === 0 && events.length > 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">No matches.</p>
              )}
            </>
          )}
        </div>
      )}

      {syncOpen && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-gray-900/50 dark:bg-black/60 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg my-8 p-5 border border-gray-200 dark:border-slate-700">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Sync Hostlinks via browser
                </h3>
                <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                  A popup cannot authenticate the VPS IP (
                  <code className="text-[10px]">149.28.68.124</code>). It uses{' '}
                  <strong>your PC’s IP</strong>, which can usually pass the bot check. Events are then
                  stored in EmailCraft for Build / queue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSyncOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ol className="text-xs text-gray-700 dark:text-slate-300 list-decimal pl-4 space-y-1 mb-4">
              <li>Open Hostlinks and complete any “Please wait” challenge (you already did this).</li>
              <li>
                If Continue fails with CORS (failed to fetch): use{' '}
                <strong>Copy console snippet</strong> — run it in the Hostlinks tab console, then paste
                JSON here.
              </li>
              <li>Click Save pasted events.</li>
            </ol>

            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                onClick={() => void openBotCheckPopup()}
                disabled={syncBusy}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                1. Open Hostlinks
              </button>
              <button
                type="button"
                onClick={() => void fetchAndCacheFromBrowser()}
                disabled={syncBusy}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {syncBusy ? 'Working…' : '2. Continue / fetch'}
              </button>
              <button
                type="button"
                onClick={() => void copyConsoleSnippet()}
                disabled={syncBusy}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50"
              >
                <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />
                Copy console snippet
              </button>
            </div>

            {syncStatus && (
              <p className="text-xs text-gray-700 dark:text-slate-300 whitespace-pre-wrap mb-3 rounded-md bg-gray-50 dark:bg-slate-900/50 p-2 border border-gray-100 dark:border-slate-700">
                {syncStatus}
              </p>
            )}

            {(showPaste ||
              syncStatus?.toLowerCase().includes('cors') ||
              syncStatus?.toLowerCase().includes('paste') ||
              syncStatus?.toLowerCase().includes('snippet') ||
              syncStatus?.toLowerCase().includes('failed to fetch')) && (
              <div className="space-y-2">
                <label className="block text-[11px] font-medium text-gray-600 dark:text-slate-400">
                  Paste Hostlinks JSON
                </label>
                <textarea
                  rows={6}
                  value={pasteJson}
                  onChange={(e) => setPasteJson(e.target.value)}
                  placeholder='[ { "id": "...", "location": "..." }, ... ]'
                  className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono"
                />
                <button
                  type="button"
                  onClick={() => void submitPaste()}
                  disabled={syncBusy || !pasteJson.trim()}
                  className="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  Save pasted events
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostlinksEventPicker;
